import { CollisionPriority } from '@dnd-kit/abstract'
import { move } from '@dnd-kit/helpers'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import z from 'zod'
import { ScorecardMutators, ScorecardSectionMutators } from '~/mutators/scorecards'
import { RouterOutput, useTRPC } from '~/services/trpc'

import { Badge, Button } from '@hap/components'

import { DeleteCriteriaItemButton } from './-components/delete-criteria-item-button'
import { ScorecardSection } from './-components/scorecard-section'

const searchSchema = z.object({
  scrollTo: z.string().optional(),
})

type SortableItems = Record<string, string[]>

export const Route = createFileRoute(
  '/_authenticated/$alias/settings/scorecards/$scorecardId/criteria/',
)({
  component: RouteComponent,
  validateSearch: searchSchema,
})

function RouteComponent() {
  const trpc = useTRPC()
  const { scorecardId } = Route.useParams()
  const { scrollTo } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [newSectionId, setNewSectionId] = useState<string | null>(null)
  const { data: scorecard } = useSuspenseQuery(
    trpc.scorecards.getById.queryOptions(scorecardId),
  )

  const { sort } = ScorecardMutators.useSort()
  const { create: createSection, isPending: isCreatingSection } =
    ScorecardSectionMutators.useCreate(scorecard.id, {
      onSuccess: (sectionId) => {
        setNewSectionId(sectionId)
      },
    })

  const [sectionOrder, setSectionOrder] = useState<string[]>(() =>
    scorecard.sections.map((s) => s.id),
  )
  const [items, setItems] = useState<SortableItems>(() => {
    return scorecard.sections.reduce<SortableItems>(
      (acc, section) => ({
        ...acc,
        [section.id]: section.criteria.map((c) => c.id),
      }),
      {},
    )
  })
  const previousItems = useRef(items)

  // Update sortable state when scorecard changes
  useEffect(() => {
    setItems(
      scorecard.sections.reduce<SortableItems>(
        (acc, section) => ({
          ...acc,
          [section.id]: section.criteria.map((c) => c.id),
        }),
        {},
      ),
    )
  }, [scorecard])

  // Handle scroll-to-item when navigating back from criteria edit
  useEffect(() => {
    if (scrollTo) {
      const timeout = setTimeout(() => {
        const element = document.querySelector(`[data-item-id="${scrollTo}"]`)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
          element.classList.add('ring-2', 'ring-primary', 'ring-offset-2')
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2')
          }, 2000)
        }
      }, 100)
      return () => clearTimeout(timeout)
    }
  }, [scrollTo])

  const [criteriaPane, setSectionId] = useState<{
    sectionId: string
    itemId?: string
  } | null>(null)

  const findItem = useCallback(
    (itemId: string) => {
      for (const section of scorecard.sections) {
        const item = section.criteria.find((c) => c.id === itemId)
        if (item) return item
      }
    },
    [scorecard],
  )

  const navigateToCriteriaEdit = useCallback(
    (itemId: string) => {
      navigate({
        to: `/$alias/settings/scorecards/${scorecard.id}/criteria/${itemId}`,
      })
    },
    [navigate, scorecard.id],
  )

  return (
    <DragDropProvider
      onDragStart={() => {
        previousItems.current = items
      }}
      onDragOver={(event) => {
        const { source } = event.operation
        if (source?.type === 'section') {
          setSectionOrder((sections) => move(sections, event))
        } else {
          setItems((currentItems) => move(currentItems, event))
        }
      }}
      onDragEnd={() => {
        sort(
          sectionOrder.reduce((acc, sectionId) => {
            return {
              ...acc,
              [sectionId]: items[sectionId],
            }
          }, {}),
        )
      }}
    >
      <div className="space-y-4">
        {sectionOrder.map((sectionId, sectionIndex) => {
          const section = scorecard.sections.find((s) => s.id === sectionId)
          if (!section) return null

          return (
            <SortableSection
              key={sectionId}
              section={section}
              sectionIndex={sectionIndex}
              workspaceId={scorecard.workspaceId}
              scorecardId={scorecard.id}
              criteriaPane={criteriaPane}
              setSectionId={setSectionId}
              isNew={newSectionId === sectionId}
              onNameCommitted={() => setNewSectionId(null)}
            >
              {items[sectionId]?.map((itemId, itemIndex) => (
                <SortableCriteriaItem
                  key={itemId}
                  itemId={itemId}
                  itemIndex={itemIndex}
                  sectionId={sectionId}
                  findItem={findItem}
                  criteriaPane={criteriaPane}
                  navigateToCriteriaEdit={navigateToCriteriaEdit}
                />
              ))}
            </SortableSection>
          )
        })}

        <div className="grid">
          <Button
            variant="secondary"
            onClick={() => createSection()}
            loading={isCreatingSection}
            loadingLabel="Adding..."
          >
            Add Section
          </Button>
        </div>
      </div>
    </DragDropProvider>
  )
}

// Sortable Section Component
function SortableSection({
  section,
  sectionIndex,
  workspaceId,
  scorecardId,
  criteriaPane,
  setSectionId,
  isNew,
  onNameCommitted,
  children,
}: {
  section: RouterOutput['scorecards']['getById']['sections'][0]
  sectionIndex: number
  workspaceId: string | null
  scorecardId: string
  criteriaPane: { sectionId: string; itemId?: string } | null
  setSectionId: (edit: { sectionId: string; itemId?: string } | null) => void
  isNew?: boolean
  onNameCommitted?: () => void
  children: React.ReactNode
}) {
  const handleRef = useRef<HTMLButtonElement | null>(null)
  const sortable = useSortable({
    id: section.id,
    index: sectionIndex,
    type: 'section',
    collisionPriority: CollisionPriority.Low,
    accept: ['section', 'criteria'],
    handle: handleRef,
  })

  return (
    <div
      ref={sortable.ref}
      data-dragging={sortable.isDragging}
      className="data-[dragging=true]:opacity-50"
    >
      <ScorecardSection
        section={section}
        workspaceId={workspaceId}
        scorecardId={scorecardId}
        criteriaPane={criteriaPane}
        setSectionId={setSectionId}
        isNew={isNew}
        onNameCommitted={onNameCommitted}
        handleRef={handleRef}
      >
        {children}
      </ScorecardSection>
    </div>
  )
}

// Sortable Criteria Item Component
function SortableCriteriaItem({
  itemId,
  itemIndex,
  sectionId,
  findItem,
  criteriaPane,
  navigateToCriteriaEdit,
}: {
  itemId: string
  itemIndex: number
  sectionId: string
  findItem: (itemId: string) =>
    | {
        id: string
        scorecardId: string
        weight?: number
        criterion: { id: string; name: string; auto?: boolean; defaultWeight?: number }
      }
    | undefined
  criteriaPane: { sectionId: string; itemId?: string } | null
  navigateToCriteriaEdit: (itemId: string) => void
}) {
  const sortable = useSortable({
    id: itemId,
    index: itemIndex,
    type: 'criteria',
    accept: 'criteria',
    group: sectionId,
  })

  const item = findItem(itemId)
  if (!item) return null

  const editing = criteriaPane?.itemId === itemId && criteriaPane?.sectionId === sectionId

  return (
    <div
      ref={sortable.ref}
      data-item-id={itemId}
      data-dragging={sortable.isDragging}
      className="dnd-item-wrapper group/criteria data-[dragging=true]:opacity-50"
    >
      <div
        className={`dnd-item bg-accent flex items-start gap-3 rounded-md ${editing ? 'bg-background border' : ''} ${sortable.isDragging ? 'dragging' : ''}`}
      >
        {editing ? (
          <div className="grow"></div>
        ) : (
          <>
            <div
              className="flex shrink grow cursor-grab py-2 font-semibold active:cursor-grabbing"
              data-cypress="draggable-item"
              tabIndex={0}
            >
              <p className="w-0 shrink grow truncate">
                {item.criterion.name}{' '}
                {item.criterion.auto && <Badge variant="success">Auto</Badge>}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="mr-4 font-semibold whitespace-nowrap">
                Weight {item.weight ?? item.criterion.defaultWeight}
              </div>
              <Button
                variant="secondary"
                size="icon"
                icon="pencil-simple"
                onClick={() => navigateToCriteriaEdit(itemId)}
              />
              <DeleteCriteriaItemButton id={item.id} scorecardId={item.scorecardId} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
